import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ComposedChart, 
  Line, 
  Area, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  ReferenceLine,
  Cell,
  Rectangle
} from 'recharts';
import { sensorAPI } from '../utils/api';
import { X, TrendingUp, Calendar, Clock, RefreshCw, Download, AlertTriangle, Settings, RotateCcw } from 'lucide-react';

interface GeneratorCombinedChartProps {
  generatorSensorId: number;
  fuelSensorId: number | null;
  generatorName: string;
  deviceId: string;
  onClose: () => void;
}

type TimePeriod = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

interface CombinedChartDataPoint {
  timestamp: string;
  formatted_time: string;
  formatted_date: string;
  fuelLevel: number | null;
  accStatus: number; // 0 or 1
  fuelDecreasingWhileOff?: boolean; // Flag for fuel decreasing when ACC is off
  fuelIncreasing?: boolean; // Flag for fuel increasing (filling)
  smoothedFuelLevel?: number; // Internal: smoothed value for detection (not displayed)
  fuelLevelNormal?: number | null; // For chart: normal fuel level (null when alert)
  fuelLevelAlert?: number | null; // For chart: alert fuel level (null when normal)
  fuelLevelFilling?: number | null; // For chart: filling fuel level (null when not filling)
}

const GeneratorCombinedChart: React.FC<GeneratorCombinedChartProps> = ({ 
  generatorSensorId, 
  fuelSensorId,
  generatorName,
  deviceId,
  onClose 
}) => {
  const [chartData, setChartData] = useState<CombinedChartDataPoint[]>([]);
  const [rawChartData, setRawChartData] = useState<CombinedChartDataPoint[]>([]); // Store raw data without alert markings
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('today');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [fillingAlerts, setFillingAlerts] = useState<any[]>([]);
  const [accRunHours, setAccRunHours] = useState<any[]>([]);
  const [consumptionRecords, setConsumptionRecords] = useState<any[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [singleDate, setSingleDate] = useState<string>('');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [showSingleDatePicker, setShowSingleDatePicker] = useState(false);
  // Track when dates are actually applied (not just selected)
  const [appliedDateRange, setAppliedDateRange] = useState<{from?: string, to?: string, single?: string} | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const isFetchingRef = useRef(false);
  
  // Default threshold values (more conservative)
  const DEFAULT_THRESHOLDS = {
    fuelDecreaseThresholdPercent: 3,
    fuelDecreaseThresholdAbsolute: 8,
    minConsecutivePoints: 3,
    smoothingWindow: 5,
    decreaseRatioThreshold: 0.5, // Minimum ratio of decreasing steps (0.0 to 1.0)
  };

  // Load thresholds from backend or use defaults
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [isCustomThresholds, setIsCustomThresholds] = useState(false);
  const [loadingThresholds, setLoadingThresholds] = useState(false);
  const [thresholdsError, setThresholdsError] = useState<string | null>(null);

  // Temporary thresholds for editing (before applying)
  const [tempThresholds, setTempThresholds] = useState(thresholds);

  // Fetch thresholds from backend on mount
  useEffect(() => {
    const fetchThresholds = async () => {
      try {
        setLoadingThresholds(true);
        const response = await sensorAPI.getFuelDecreaseThresholds(generatorSensorId);
        if (response.thresholds) {
          const fetchedThresholds = {
            fuelDecreaseThresholdPercent: response.thresholds.fuel_decrease_threshold_percent,
            fuelDecreaseThresholdAbsolute: response.thresholds.fuel_decrease_threshold_absolute,
            minConsecutivePoints: response.thresholds.min_consecutive_points,
            smoothingWindow: response.thresholds.smoothing_window,
            decreaseRatioThreshold: response.thresholds.decrease_ratio_threshold,
            fuelIncreaseThresholdPercent: response.thresholds.fuel_increase_threshold_percent ?? 3.0,
            fuelIncreaseThresholdAbsolute: response.thresholds.fuel_increase_threshold_absolute ?? 8.0,
            minFillingDurationMinutes: response.thresholds.min_filling_duration_minutes ?? 2,
          };
          setThresholds(fetchedThresholds);
          setTempThresholds(fetchedThresholds);
          setIsCustomThresholds(response.is_custom || false);
        }
      } catch (error: any) {
        console.error('Error fetching thresholds:', error);
        setThresholdsError('Failed to load thresholds. Using defaults.');
      } finally {
        setLoadingThresholds(false);
      }
    };
    
    fetchThresholds();
  }, [generatorSensorId]);

  // Update temp thresholds when actual thresholds change
  useEffect(() => {
    setTempThresholds(thresholds);
  }, [thresholds]);

  // Apply thresholds locally (on-the-fly) without saving to backend
  const applyThresholds = () => {
    console.log('Applying thresholds on-the-fly:', tempThresholds);
    console.log('Raw chart data length:', rawChartData.length);
    console.log('Chart data length:', chartData.length);
    
    setThresholds(tempThresholds);
    
    // Get the raw data to recalculate from (prefer rawChartData, fallback to chartData)
    const dataToUse = rawChartData.length > 0 ? rawChartData : chartData;
    
    if (dataToUse.length > 0) {
      // When applying on-the-fly, we want to recalculate everything with new thresholds
      // So we pass empty arrays for backend alerts to force frontend detection
      const updatedData = applyDetectionToData(dataToUse, [], [], tempThresholds);
      console.log('Updated data points:', updatedData.length);
      console.log('Points with decrease alerts:', updatedData.filter(p => p.fuelDecreasingWhileOff).length);
      console.log('Points with filling alerts:', updatedData.filter(p => p.fuelIncreasing).length);
      setChartData(updatedData);
    } else {
      console.warn('No data available to apply thresholds to');
    }
  };

  // Reset to defaults (from backend or sensor config)
  const resetThresholds = async () => {
    try {
      // Fetch current thresholds from backend (which includes sensor defaults)
      const response = await sensorAPI.getFuelDecreaseThresholds(generatorSensorId);
      if (response.thresholds) {
        const fetchedThresholds = {
          fuelDecreaseThresholdPercent: response.thresholds.fuel_decrease_threshold_percent,
          fuelDecreaseThresholdAbsolute: response.thresholds.fuel_decrease_threshold_absolute,
          minConsecutivePoints: response.thresholds.min_consecutive_points,
          smoothingWindow: response.thresholds.smoothing_window,
          decreaseRatioThreshold: response.thresholds.decrease_ratio_threshold,
          fuelIncreaseThresholdPercent: response.thresholds.fuel_increase_threshold_percent ?? 3.0,
          fuelIncreaseThresholdAbsolute: response.thresholds.fuel_increase_threshold_absolute ?? 8.0,
          minFillingDurationMinutes: response.thresholds.min_filling_duration_minutes ?? 2,
        };
        setTempThresholds(fetchedThresholds);
        setThresholds(fetchedThresholds);
      } else {
        // Fallback to hardcoded defaults
        setTempThresholds(DEFAULT_THRESHOLDS);
        setThresholds(DEFAULT_THRESHOLDS);
      }
    } catch (error) {
      console.error('Error fetching thresholds for reset:', error);
      // Fallback to hardcoded defaults
      setTempThresholds(DEFAULT_THRESHOLDS);
      setThresholds(DEFAULT_THRESHOLDS);
    }
  };
  
  const deleteUserThresholds = async () => {
    try {
      setLoadingThresholds(true);
      setThresholdsError(null);
      await sensorAPI.deleteFuelDecreaseThresholds(generatorSensorId);
      // Reload thresholds (will get group/global defaults)
      const response = await sensorAPI.getFuelDecreaseThresholds(generatorSensorId);
      if (response.thresholds) {
        setThresholds({
          fuelDecreaseThresholdPercent: response.thresholds.fuel_decrease_threshold_percent,
          fuelDecreaseThresholdAbsolute: response.thresholds.fuel_decrease_threshold_absolute,
          minConsecutivePoints: response.thresholds.min_consecutive_points,
          smoothingWindow: response.thresholds.smoothing_window,
          decreaseRatioThreshold: response.thresholds.decrease_ratio_threshold,
        });
        setIsCustomThresholds(false);
        setTempThresholds({
          fuelDecreaseThresholdPercent: response.thresholds.fuel_decrease_threshold_percent,
          fuelDecreaseThresholdAbsolute: response.thresholds.fuel_decrease_threshold_absolute,
          minConsecutivePoints: response.thresholds.min_consecutive_points,
          smoothingWindow: response.thresholds.smoothing_window,
          decreaseRatioThreshold: response.thresholds.decrease_ratio_threshold,
        });
      }
      // Refresh chart data
      await fetchChartData(selectedPeriod, true);
    } catch (error: any) {
      console.error('Error deleting thresholds:', error);
      setThresholdsError('Failed to reset thresholds.');
    } finally {
      setLoadingThresholds(false);
    }
  };

  const periods = [
    { value: 'today' as TimePeriod, label: 'Today', icon: Clock },
    { value: 'yesterday' as TimePeriod, label: 'Yesterday', icon: Calendar },
    { value: 'week' as TimePeriod, label: 'Last Week', icon: TrendingUp },
    { value: 'month' as TimePeriod, label: 'Last 30 Days', icon: TrendingUp },
  ];

  const fetchChartData = async (
    period: TimePeriod, 
    isRefresh = false, 
    signal?: AbortSignal, 
    requestId?: number,
    customFromDate?: string,
    customToDate?: string,
    customSingleDate?: string
  ) => {
    // Check if component is still mounted and request not aborted
    if (signal?.aborted || !isMountedRef.current) {
      return;
    }

    // Store the request ID for this call
    const currentRequestId = requestId ?? requestIdRef.current;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      // Fetch both generator (ACC) and fuel sensor history with date range support
      const [generatorHistory, fuelHistory] = await Promise.all([
        sensorAPI.getSensorHistory(
          generatorSensorId, 
          period === 'custom' ? undefined : period,
          customFromDate,
          customToDate,
          customSingleDate
        ),
        fuelSensorId ? sensorAPI.getSensorHistory(
          fuelSensorId, 
          period === 'custom' ? undefined : period,
          customFromDate,
          customToDate,
          customSingleDate
        ) : Promise.resolve(null)
      ]);

      // Check again after async operations - ensure this is still the latest request
      if (signal?.aborted || !isMountedRef.current || requestIdRef.current !== currentRequestId) {
        return;
      }

      // Combine the data by timestamp
      const combinedData = combineHistoryData(
        generatorHistory.chart_data || [],
        fuelHistory?.chart_data || [],
        generatorHistory.sensor.device_id
      );

      // Store raw data for threshold recalculation
      setRawChartData(combinedData);

      // Get alerts from backend (included in generator history response)
      const backendAlerts = generatorHistory.alerts || [];
      const backendFillingAlerts = generatorHistory.filling_alerts || [];
      const backendAccRunHours = generatorHistory.acc_run_hours || [];
      const backendConsumptionRecords = generatorHistory.consumption_records || [];
      setAlerts(backendAlerts);
      setFillingAlerts(backendFillingAlerts);
      setAccRunHours(backendAccRunHours);
      setConsumptionRecords(backendConsumptionRecords);

      // Apply detection to mark data points (includes both decrease and increase alerts)
      const chartDataPrepared = applyDetectionToData(combinedData, backendAlerts, backendFillingAlerts, thresholds);
      
      // Debug: Log alerts and marked points
      console.log('Backend alerts:', backendAlerts);
      console.log('Points with alerts:', chartDataPrepared.filter(p => p.fuelDecreasingWhileOff).length);
      
      // Final check before setting state - ensure this is still the latest request
      if (signal?.aborted || !isMountedRef.current || requestIdRef.current !== currentRequestId) {
        return;
      }

      setChartData(chartDataPrepared);
    } catch (err: any) {
      // Don't set error if request was aborted or superseded
      if (signal?.aborted || !isMountedRef.current || requestIdRef.current !== currentRequestId) {
        return;
      }
      console.error('Error fetching chart data:', err);
      setError(err.response?.data?.error || 'Failed to load chart data');
    } finally {
      // Mark as no longer fetching
      if (requestIdRef.current === currentRequestId) {
        isFetchingRef.current = false;
      }
      // Only update loading state if not aborted
      if (!signal?.aborted && isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  /**
   * Apply detection to data points using backend alerts and/or frontend detection
   * This allows real-time threshold updates without refetching data
   */
  const applyDetectionToData = (
    data: CombinedChartDataPoint[],
    backendAlerts: any[],
    backendFillingAlerts: any[],
    thresholdsToUse: typeof thresholds
  ): CombinedChartDataPoint[] => {
    if (data.length < 3) return data;

    // Clean the data first - remove any existing alert markings to start fresh
    const cleanData: CombinedChartDataPoint[] = data.map(point => ({
      ...point,
      fuelDecreasingWhileOff: false,
      fuelIncreasing: false,
      fuelLevelNormal: point.fuelLevel,
      fuelLevelAlert: null,
      fuelLevelFilling: null,
    }));

    // If backend alerts are provided, mark points based on them
    let markedData: CombinedChartDataPoint[] = cleanData;
    if (backendAlerts.length > 0 || backendFillingAlerts.length > 0) {
      markedData = cleanData.map((point): CombinedChartDataPoint => {
        const pointTimestamp = new Date(point.timestamp).getTime();
        
        // Check decrease alerts
        const hasDecreaseAlert = backendAlerts.some((alert: any) => {
          try {
            const alertStart = new Date(alert.start_timestamp).getTime();
            const alertEnd = new Date(alert.end_timestamp).getTime();
            return pointTimestamp >= alertStart && pointTimestamp <= alertEnd && point.accStatus === 0 && point.fuelLevel !== null;
          } catch (e) {
            console.warn('Error parsing alert timestamp:', e);
            return false;
          }
        });
        
        // Check filling alerts
        const hasFillingAlert = backendFillingAlerts.some((alert: any) => {
          try {
            const alertStart = new Date(alert.start_timestamp).getTime();
            const alertEnd = new Date(alert.end_timestamp).getTime();
            return pointTimestamp >= alertStart && pointTimestamp <= alertEnd && point.fuelLevel !== null;
          } catch (e) {
            console.warn('Error parsing filling alert timestamp:', e);
            return false;
          }
        });

        return {
          ...point,
          fuelDecreasingWhileOff: hasDecreaseAlert,
          fuelIncreasing: hasFillingAlert,
          fuelLevelNormal: hasDecreaseAlert || hasFillingAlert ? null : point.fuelLevel,
          fuelLevelAlert: hasDecreaseAlert ? point.fuelLevel : null,
          fuelLevelFilling: hasFillingAlert ? point.fuelLevel : null,
        };
      });
    }

    // Always run frontend detection with the provided thresholds
    // This allows real-time threshold updates
    const frontendDetectedData = detectFuelDecreasingWhileOff(markedData, thresholdsToUse, backendAlerts);
    
    // Detect fuel increases (filling) - frontend detection
    const dataWithFilling = detectFuelIncreasing(frontendDetectedData, thresholdsToUse);

    return dataWithFilling;
  };

  /**
   * Frontend detection algorithm for real-time threshold updates
   * This runs on existing data without needing to fetch from API
   */
  const detectFuelDecreasingWhileOff = (
    data: CombinedChartDataPoint[], 
    thresholdsToUse: typeof thresholds = thresholds,
    backendAlerts: any[] = []
  ): CombinedChartDataPoint[] => {
    if (data.length < 3) return data;
    
    const FUEL_DECREASE_THRESHOLD_PERCENT = thresholdsToUse.fuelDecreaseThresholdPercent;
    const FUEL_DECREASE_THRESHOLD_ABSOLUTE = thresholdsToUse.fuelDecreaseThresholdAbsolute;
    const MIN_CONSECUTIVE_POINTS = thresholdsToUse.minConsecutivePoints;
    const SMOOTHING_WINDOW = thresholdsToUse.smoothingWindow;
    const DECREASE_RATIO_THRESHOLD = thresholdsToUse.decreaseRatioThreshold;
    
    // Step 1: Apply moving average smoothing to reduce sensor noise
    const smoothedData = data.map((point, index) => {
      if (point.fuelLevel === null) return point;
      
      const windowStart = Math.max(0, index - Math.floor(SMOOTHING_WINDOW / 2));
      const windowEnd = Math.min(data.length, index + Math.ceil(SMOOTHING_WINDOW / 2));
      const window = data.slice(windowStart, windowEnd).filter((p: CombinedChartDataPoint) => p.fuelLevel !== null);
      
      if (window.length === 0) return point;
      
      const avgFuel = window.reduce((sum, p) => sum + (p.fuelLevel || 0), 0) / window.length;
      
      return {
        ...point,
        smoothedFuelLevel: avgFuel
      };
    });

    // Step 2: Find periods where ACC is off (generator is off)
    const alertIndices = new Set<number>();
    
    for (let i = 0; i < smoothedData.length; i++) {
      if (smoothedData[i].accStatus !== 0 || smoothedData[i].fuelLevel === null) continue;
      
      // Find the end of this ACC-off period
      let periodEnd = i;
      while (periodEnd < smoothedData.length - 1 && smoothedData[periodEnd + 1].accStatus === 0) {
        periodEnd++;
      }
      
      if (periodEnd - i < MIN_CONSECUTIVE_POINTS) continue;
      
      const periodStartFuel = smoothedData[i].smoothedFuelLevel ?? smoothedData[i].fuelLevel ?? 0;
      const periodEndFuel = smoothedData[periodEnd].smoothedFuelLevel ?? smoothedData[periodEnd].fuelLevel ?? 0;
      
      if (periodStartFuel <= periodEndFuel) continue; // Not decreasing
      
      const periodDecrease = periodStartFuel - periodEndFuel;
      const percentDecrease = (periodDecrease / periodStartFuel) * 100;
      
      // Check thresholds
      const exceedsThreshold = percentDecrease > FUEL_DECREASE_THRESHOLD_PERCENT || 
                              periodDecrease > FUEL_DECREASE_THRESHOLD_ABSOLUTE;
      
      if (!exceedsThreshold) continue;
      
      // Verify sustained decrease
      let decreasingSteps = 0;
      let totalSteps = 0;
      
      for (let k = i + 1; k <= periodEnd; k++) {
        const prevPoint = smoothedData[k - 1];
        const currPoint = smoothedData[k];
        
        if (prevPoint.fuelLevel === null || currPoint.fuelLevel === null ||
            !prevPoint.smoothedFuelLevel || !currPoint.smoothedFuelLevel) {
          continue;
        }
        
        totalSteps++;
        const stepChange = prevPoint.smoothedFuelLevel - currPoint.smoothedFuelLevel;
        if (stepChange > 0) {
          decreasingSteps++;
        }
      }
      
      if (totalSteps === 0) continue;
      
      const decreaseRatio = decreasingSteps / totalSteps;
      if (decreaseRatio < DECREASE_RATIO_THRESHOLD) continue;
      
      // Additional check: Require minimum rate of decrease to filter out gradual drift
      try {
        const periodStartTime = new Date(smoothedData[i].timestamp);
        const periodEndTime = new Date(smoothedData[periodEnd].timestamp);
        const periodDuration = (periodEndTime.getTime() - periodStartTime.getTime()) / 1000; // seconds
        const periodDurationHours = periodDuration / 3600;
        
        if (periodDurationHours > 0) {
          const decreaseRatePerHour = periodDecrease / periodDurationHours;
          
          // For gradual decreases (low rate), require much higher total decrease
          // This filters out sensor drift/fluctuation
          if (decreaseRatePerHour < 2.0) { // Less than 2L per hour
            // Require at least 2x the normal thresholds for gradual decreases
            if (percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * 2 || 
                periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * 2) {
              continue;
            }
            // Also require very high decrease ratio (95%) for gradual decreases
            if (decreaseRatio < 0.95) {
              continue;
            }
          }
          
          // For very gradual decreases (less than 1L per hour), require even higher thresholds
          if (decreaseRatePerHour < 1.0) {
            // Require at least 3x the normal thresholds
            if (percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * 3 || 
                periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * 3) {
              continue;
            }
          }
        }
      } catch (e) {
        // If timestamp parsing fails, skip this check
      }
      
      // Check for fluctuation pattern - if fuel goes up and down, it's likely noise
      let directionChanges = 0;
      let significantIncreases = 0; // Count significant increases (>0.5L)
      let prevDirection: number | null = null; // 1 = increasing, -1 = decreasing, 0 = stable
      let maxFuelDuringPeriod = periodStartFuel;
      let minFuelDuringPeriod = periodStartFuel;
      const periodPoints: number[] = [];
      
      for (let k = i; k <= periodEnd; k++) {
        const point = smoothedData[k];
        if (point && point.smoothedFuelLevel !== undefined) {
          periodPoints.push(point.smoothedFuelLevel);
          if (point.smoothedFuelLevel > maxFuelDuringPeriod) {
            maxFuelDuringPeriod = point.smoothedFuelLevel;
          }
          if (point.smoothedFuelLevel < minFuelDuringPeriod) {
            minFuelDuringPeriod = point.smoothedFuelLevel;
          }
        }
      }
      
      for (let k = i + 1; k <= periodEnd; k++) {
        const prevPoint = smoothedData[k-1];
        const currPoint = smoothedData[k];
        
        if (!prevPoint || !currPoint) continue;
        if (prevPoint.smoothedFuelLevel === undefined || currPoint.smoothedFuelLevel === undefined) {
          continue;
        }
        
        const change = currPoint.smoothedFuelLevel - prevPoint.smoothedFuelLevel;
        const currentDirection = change > 0.1 ? 1 : (change < -0.1 ? -1 : 0);
        
        // Count significant increases (fuel going back up)
        // Use higher threshold (1L) to avoid counting minor sensor fluctuations
        if (change > 1.0) {
          significantIncreases++;
        }
        
        if (prevDirection !== null && currentDirection !== 0 && prevDirection !== 0 && currentDirection !== prevDirection) {
          directionChanges++;
        }
        prevDirection = currentDirection;
      }
      
      // Calculate period variance - high variance indicates fluctuation
      let periodVariance = 0;
      if (periodPoints.length > 1) {
        const periodMean = periodPoints.reduce((sum, p) => sum + p, 0) / periodPoints.length;
        const varianceSum = periodPoints.reduce((sum, p) => {
          const diff = p - periodMean;
          return sum + (diff * diff);
        }, 0);
        periodVariance = Math.sqrt(varianceSum / periodPoints.length);
      }
      
      // For very large decreases, be more lenient with fluctuations
      const isVeryLargeDecrease = percentDecrease > 15 || periodDecrease > 30;
      
      // If there are significant increases during the period, it's likely fluctuation
      // But be more lenient for very large decreases
      if (significantIncreases > 0) {
        // If fuel increased by more than 2L at any point (not just 1L), require higher thresholds
        // For very large decreases, allow up to 3L increase
        const maxAllowedIncrease = isVeryLargeDecrease ? 3.0 : 1.0;
        if (maxFuelDuringPeriod - periodStartFuel > maxAllowedIncrease) {
          const requiredMultiplier = isVeryLargeDecrease ? 2.0 : 3.0;
          if (percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * requiredMultiplier || 
              periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * requiredMultiplier) {
            continue;
          }
        }
        // If there are many increases, require higher thresholds
        // But be more lenient for very large decreases
        const minIncreaseCount = isVeryLargeDecrease ? 5 : 1;
        if (significantIncreases > minIncreaseCount) {
          const requiredRatio = isVeryLargeDecrease ? 0.75 : 0.9;
          const requiredMultiplier = isVeryLargeDecrease ? 1.5 : 2.0;
          if (decreaseRatio < requiredRatio || 
              percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * requiredMultiplier || 
              periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * requiredMultiplier) {
            continue;
          }
        }
      }
      
      // If variance is high relative to the decrease, it's likely fluctuation
      // But be more lenient for very large decreases
      if (periodVariance > 0 && periodDecrease > 0) {
        const varianceToDecreaseRatio = periodVariance / periodDecrease;
        // For very large decreases, allow higher variance (50% vs 30%)
        const maxVarianceRatio = isVeryLargeDecrease ? 0.5 : 0.3;
        if (varianceToDecreaseRatio > maxVarianceRatio) {
          // Require higher thresholds and higher decrease ratio
          // But be more lenient for very large decreases
          const requiredRatio = isVeryLargeDecrease ? 0.7 : 0.85;
          const requiredMultiplier = isVeryLargeDecrease ? 1.5 : 2.0;
          if (decreaseRatio < requiredRatio || 
              percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * requiredMultiplier || 
              periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * requiredMultiplier) {
            continue;
          }
        }
      }
      
      // If there are many direction changes, it's likely fluctuation, not real decrease
      // But be more lenient for very large decreases
      if (directionChanges > 3) {
        const requiredRatio = isVeryLargeDecrease ? 0.65 : 0.8;
        const requiredMultiplier = isVeryLargeDecrease ? 1.5 : 2.0;
        if (decreaseRatio < requiredRatio) {
          continue;
        }
        // Also require higher thresholds when there are fluctuations
        // But be more lenient for very large decreases
        if (percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * requiredMultiplier || 
            periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * requiredMultiplier) {
          continue;
        }
      }
      
      // Mark all points in this period
      for (let k = i; k <= periodEnd; k++) {
        alertIndices.add(k);
      }
    }
    
    // Step 3: Apply markings to data
    return smoothedData.map((point, index) => {
      const hasAlert = alertIndices.has(index) && point.accStatus === 0 && point.fuelLevel !== null;
      // When thresholds are applied on-the-fly, we recalculate everything
      // So we only use the frontend detection result, not backend alerts
      const hasBackendAlert = point.fuelDecreasingWhileOff === true && backendAlerts.length > 0;
      const finalHasAlert = hasAlert || hasBackendAlert;
      
      return {
        ...point,
        fuelDecreasingWhileOff: finalHasAlert,
        fuelLevelNormal: finalHasAlert ? null : point.fuelLevel,
        fuelLevelAlert: finalHasAlert ? point.fuelLevel : null,
      };
    });
  };

  /**
   * Detect fuel increases (filling diesel)
   * Optimized to detect significant, sustained increases while filtering out sensor noise
   * This matches the backend algorithm exactly
   */
  const detectFuelIncreasing = (
    data: CombinedChartDataPoint[],
    thresholdsToUse: typeof thresholds = thresholds
  ): CombinedChartDataPoint[] => {
    if (data.length < 2) return data;

    const FUEL_INCREASE_THRESHOLD_PERCENT = (thresholdsToUse as any).fuelIncreaseThresholdPercent ?? thresholdsToUse.fuelDecreaseThresholdPercent;
    const FUEL_INCREASE_THRESHOLD_ABSOLUTE = (thresholdsToUse as any).fuelIncreaseThresholdAbsolute ?? thresholdsToUse.fuelDecreaseThresholdAbsolute;
    const MIN_DURATION_SECONDS = ((thresholdsToUse as any).minFillingDurationMinutes ?? 2) * 60;
    
    // Stricter thresholds to filter out sensor noise
    const MIN_ABSOLUTE_INCREASE = Math.max(5.0, FUEL_INCREASE_THRESHOLD_ABSOLUTE); // At least 5L or threshold, whichever is higher
    const MAX_FILLING_DURATION = 3600; // 60 minutes max for filling detection
    const MIN_SUSTAINED_POINTS = 2; // Need at least 2 consecutive increasing points

    const fillingIndices = new Set<number>();
    
    // Step 1: Detect sudden spikes (large jumps between consecutive points)
    // Only detect if BOTH percentage AND absolute thresholds are met
    for (let i = 1; i < data.length; i++) {
      const prevPoint = data[i - 1];
      const currPoint = data[i];
      
      if (prevPoint.fuelLevel === null || currPoint.fuelLevel === null) continue;
      
      const fuelIncrease = currPoint.fuelLevel - prevPoint.fuelLevel;
      if (fuelIncrease <= 0) continue;
      
      const percentIncrease = prevPoint.fuelLevel > 0 ? (fuelIncrease / prevPoint.fuelLevel) * 100 : 0;
      
      // Require BOTH absolute AND percentage thresholds (not OR)
      // Also require minimum absolute increase to filter out small fluctuations
      const meetsAbsoluteThreshold = fuelIncrease >= MIN_ABSOLUTE_INCREASE;
      const meetsPercentThreshold = percentIncrease >= FUEL_INCREASE_THRESHOLD_PERCENT;
      
      // For very large increases, be more lenient with percentage
      const isVeryLargeIncrease = fuelIncrease >= MIN_ABSOLUTE_INCREASE * 2;
      
      const isSpike = meetsAbsoluteThreshold && (meetsPercentThreshold || isVeryLargeIncrease);
      
      if (isSpike) {
        // Check if increase is sustained (not immediately followed by decrease)
        let isSustained = true;
        if (i + 1 < data.length && data[i + 1].fuelLevel !== null) {
          const nextFuel = data[i + 1].fuelLevel;
          // If fuel drops significantly after increase, it might be noise
          if (nextFuel !== null && nextFuel < currPoint.fuelLevel - (fuelIncrease * 0.3)) {
            isSustained = false;
          }
        }
        
        if (isSustained) {
          // Mark the spike point and a few surrounding points
          fillingIndices.add(i - 1);
          fillingIndices.add(i);
          // Also mark next few points to show the filling event visually
          for (let j = i + 1; j < Math.min(data.length, i + 3); j++) {
            if (data[j].fuelLevel !== null) {
              fillingIndices.add(j);
            }
          }
        }
      }
    }
    
    // Step 2: Detect rapid increases over short periods (3-10 points)
    // This catches filling events that happen over multiple data points
    for (let i = 0; i < data.length - 3; i++) {
      if (data[i].fuelLevel === null) continue;
      if (fillingIndices.has(i)) continue; // Already detected as spike
      
      // Look ahead up to 10 points for sustained increase
      let periodEnd = i;
      let maxIncrease = 0;
      let increasingPoints = 0;
      let totalPoints = 0;
      
      for (let j = i + 1; j < Math.min(data.length, i + 11); j++) {
        if (data[j].fuelLevel === null) break;
        
        const startFuel = data[i].fuelLevel ?? 0;
        const currFuel = data[j].fuelLevel ?? 0;
        const increase = currFuel - startFuel;
        
        totalPoints++;
        
        // Count increasing steps
        if (j > i + 1) {
          const prevFuel = data[j - 1].fuelLevel ?? 0;
          if (currFuel > prevFuel) {
            increasingPoints++;
          }
        }
        
        if (increase > maxIncrease) {
          maxIncrease = increase;
          periodEnd = j;
        }
        
        // If we see a significant decrease (more than 30% of current increase), stop looking
        if (j > i + 1 && maxIncrease > 0) {
          const prevFuel = data[j - 1].fuelLevel ?? 0;
          if (currFuel !== null && prevFuel !== null && currFuel < prevFuel && (prevFuel - currFuel) > (maxIncrease * 0.3)) {
            break;
          }
        }
      }
      
      if (maxIncrease <= 0 || totalPoints < MIN_SUSTAINED_POINTS) continue;
      
      // Require at least 50% of steps to be increasing
      const increaseRatio = totalPoints > 0 ? increasingPoints / (totalPoints - 1) : 0;
      if (increaseRatio < 0.5) continue;
      
      const startFuel = data[i].fuelLevel ?? 0;
      const percentIncrease = startFuel > 0 ? (maxIncrease / startFuel) * 100 : 0;
      
      // Require BOTH absolute AND percentage thresholds
      const meetsAbsoluteThreshold = maxIncrease >= MIN_ABSOLUTE_INCREASE;
      const meetsPercentThreshold = percentIncrease >= FUEL_INCREASE_THRESHOLD_PERCENT;
      
      // For very large increases, be more lenient with percentage
      const isVeryLargeIncrease = maxIncrease >= MIN_ABSOLUTE_INCREASE * 2;
      
      const exceedsThreshold = meetsAbsoluteThreshold && (meetsPercentThreshold || isVeryLargeIncrease);
      
      if (!exceedsThreshold) continue;
      
      // Check time duration
      try {
        const periodStartTime = new Date(data[i].timestamp);
        const periodEndTime = new Date(data[periodEnd].timestamp);
        const periodDuration = (periodEndTime.getTime() - periodStartTime.getTime()) / 1000;
        
        // Duration should be reasonable for filling (not too long, not too short)
        if (periodDuration > MAX_FILLING_DURATION || periodDuration < 60) continue;
        
        // Calculate rate of increase (L per minute)
        const rateOfIncrease = periodDuration > 0 ? (maxIncrease / (periodDuration / 60)) : 0;
        
        // For smaller increases, require higher rate (faster filling)
        // For larger increases, allow lower rate
        const minRate = maxIncrease < MIN_ABSOLUTE_INCREASE * 2 ? 3.0 : 1.0; // L per minute
        if (rateOfIncrease < minRate && maxIncrease < MIN_ABSOLUTE_INCREASE * 2) {
          continue;
        }
      } catch (e) {
        // If timestamp parsing fails, skip this check
        continue;
      }
      
      // Mark points in this rapid filling period
      for (let k = i; k <= periodEnd; k++) {
        if (data[k].fuelLevel !== null) {
          fillingIndices.add(k);
        }
      }
    }
    
    // Step 3: Apply markings to data
    return data.map((point, index) => {
      const isFilling = fillingIndices.has(index);
      
      return {
        ...point,
        fuelIncreasing: isFilling,
        fuelLevelFilling: isFilling ? point.fuelLevel : null,
        // Adjust normal/alert levels to account for filling
        fuelLevelNormal: point.fuelDecreasingWhileOff ? null : 
                        (isFilling ? null : point.fuelLevel),
        fuelLevelAlert: point.fuelDecreasingWhileOff ? point.fuelLevel : null,
      };
    });
  };

  /**
   * Calculate ACC run hours from chart data (on-the-fly calculation)
   */
  const calculateAccRunHours = (data: CombinedChartDataPoint[]): number => {
    if (data.length < 2) return 0;

    let totalRunHours = 0;
    let currentPeriodStart: Date | null = null;
    let previousStatus: number | null = null;

    for (const point of data) {
      const timestamp = new Date(point.timestamp);
      const accStatus = point.accStatus ?? 0;

      // Handle first data point - if ACC is ON, start tracking
      if (previousStatus === null) {
        if (accStatus === 1) {
          currentPeriodStart = timestamp;
        }
        previousStatus = accStatus;
        continue;
      }

      // Detect when ACC turns ON (transition from 0 to 1)
      if (previousStatus === 0 && accStatus === 1) {
        currentPeriodStart = timestamp;
      }

      // Detect when ACC turns OFF (transition from 1 to 0)
      if (previousStatus === 1 && accStatus === 0) {
        if (currentPeriodStart !== null) {
          const periodDuration = (timestamp.getTime() - currentPeriodStart.getTime()) / 1000; // seconds
          const periodHours = periodDuration / 3600;
          totalRunHours += periodHours;
          currentPeriodStart = null;
        }
      }

      previousStatus = accStatus;
    }

    // Handle case where ACC is still ON at end of data
    if (currentPeriodStart !== null && data.length > 0) {
      const lastTimestamp = new Date(data[data.length - 1].timestamp);
      const periodDuration = (lastTimestamp.getTime() - currentPeriodStart.getTime()) / 1000;
      const periodHours = periodDuration / 3600;
      totalRunHours += periodHours;
    }

    return totalRunHours;
  };

  /**
   * Calculate total fuel filled (summation of increases)
   * Uses backend filling alerts for accurate calculation
   */
  const calculateTotalFilling = (data: CombinedChartDataPoint[], fillingAlertsData: any[] = []): number => {
    // First, try to calculate from backend filling alerts (more accurate)
    if (fillingAlertsData.length > 0) {
      const totalFromAlerts = fillingAlertsData.reduce((sum: number, alert: any) => {
        return sum + (alert.increase_amount || 0);
      }, 0);
      if (totalFromAlerts > 0) {
        return totalFromAlerts;
      }
    }
    
    // Fallback to frontend calculation from data points
    if (data.length < 2) return 0;
    
    let totalFilled = 0;
    let inFillingPeriod = false;
    
    for (let i = 1; i < data.length; i++) {
      const prevPoint = data[i - 1];
      const currPoint = data[i];
      
      if (prevPoint.fuelLevel === null || currPoint.fuelLevel === null) {
        if (inFillingPeriod) {
          inFillingPeriod = false;
        }
        continue;
      }
      
      const isFilling = currPoint.fuelIncreasing === true;
      const fuelIncrease = currPoint.fuelLevel - prevPoint.fuelLevel;
      
      if (isFilling && fuelIncrease > 0) {
        if (!inFillingPeriod) {
          inFillingPeriod = true;
        }
        totalFilled += fuelIncrease;
      } else if (inFillingPeriod && (!isFilling || fuelIncrease <= 0)) {
        inFillingPeriod = false;
      }
    }
    
    return Math.max(0, totalFilled);
  };

  const combineHistoryData = (
    accData: any[],
    fuelData: any[],
    deviceId: string
  ): CombinedChartDataPoint[] => {
    // Create a map of timestamps to data points
    const dataMap = new Map<string, CombinedChartDataPoint>();

    // Process ACC data (from generator sensor - ignition status)
    // The generator sensor history should contain ACC status (0 or 1)
    accData.forEach((point) => {
      const timestamp = point.timestamp;
      const accValue = point.value !== null && point.value !== undefined 
        ? (Number(point.value) > 0 ? 1 : 0) 
        : 0;
      
      if (!dataMap.has(timestamp)) {
        dataMap.set(timestamp, {
          timestamp,
          formatted_time: point.formatted_time || new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          formatted_date: point.formatted_date || new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          fuelLevel: null,
          accStatus: accValue,
        });
      } else {
        const existing = dataMap.get(timestamp)!;
        existing.accStatus = accValue;
      }
    });

    // Process fuel data
    fuelData.forEach((point) => {
      const timestamp = point.timestamp;
      if (dataMap.has(timestamp)) {
        const existing = dataMap.get(timestamp)!;
        existing.fuelLevel = point.value !== null && point.value !== undefined ? Number(point.value) : null;
      } else {
        dataMap.set(timestamp, {
          timestamp,
          formatted_time: point.formatted_time || new Date(timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          formatted_date: point.formatted_date || new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          fuelLevel: point.value !== null && point.value !== undefined ? Number(point.value) : null,
          accStatus: 0, // Default to off if no ACC data
        });
      }
    });

    // Convert map to array and sort by timestamp
    const combined = Array.from(dataMap.values());
    combined.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return combined;
  };

  // Detection is now done on the backend via daily cron job
  // Alerts are fetched from the API and displayed on the chart
  // This function is kept for reference but no longer used
  const _detectFuelDecreasingWhileOff_DEPRECATED = (
    data: CombinedChartDataPoint[], 
    thresholdsToUse: typeof thresholds = thresholds
  ): CombinedChartDataPoint[] => {
    if (data.length < 3) return data; // Need at least 3 points for meaningful analysis
    
    // Use provided thresholds or current state thresholds
    const FUEL_DECREASE_THRESHOLD_PERCENT = thresholdsToUse.fuelDecreaseThresholdPercent;
    const FUEL_DECREASE_THRESHOLD_ABSOLUTE = thresholdsToUse.fuelDecreaseThresholdAbsolute;
    const MIN_CONSECUTIVE_POINTS = thresholdsToUse.minConsecutivePoints;
    const SMOOTHING_WINDOW = thresholdsToUse.smoothingWindow;
    const DECREASE_RATIO_THRESHOLD = thresholdsToUse.decreaseRatioThreshold;
    
    // Step 1: Apply moving average smoothing to reduce sensor noise
    const smoothedData = data.map((point, index) => {
      if (point.fuelLevel === null) return point;
      
      // Calculate moving average
      const windowStart = Math.max(0, index - Math.floor(SMOOTHING_WINDOW / 2));
      const windowEnd = Math.min(data.length, index + Math.ceil(SMOOTHING_WINDOW / 2));
      const window = data.slice(windowStart, windowEnd).filter(p => p.fuelLevel !== null);
      
      if (window.length === 0) return point;
      
      const avgFuel = window.reduce((sum, p) => sum + (p.fuelLevel || 0), 0) / window.length;
      
      return {
        ...point,
        smoothedFuelLevel: avgFuel
      };
    });
    
    // Step 2: Find all periods where ACC is off and analyze fuel changes
    const flaggedIndices = new Set<number>();
    
    for (let i = 0; i < smoothedData.length; i++) {
      // Find the start of an ACC-off period
      if (smoothedData[i].accStatus !== 0) continue;
      if (smoothedData[i].fuelLevel === null || smoothedData[i].smoothedFuelLevel === undefined) continue;
      
      // Find the end of this ACC-off period (until ACC turns on or data ends)
      let periodEnd = i;
      for (let j = i + 1; j < smoothedData.length; j++) {
        if (smoothedData[j].accStatus !== 0) {
          periodEnd = j - 1;
          break;
        }
        if (j === smoothedData.length - 1) {
          periodEnd = j;
        }
      }
      
      // Need at least MIN_CONSECUTIVE_POINTS points in this period
      if (periodEnd - i + 1 < MIN_CONSECUTIVE_POINTS) continue;
      
      // Step 3: Check baseline stability BEFORE the ACC-off period
      // Look back to find when ACC was last on (or start of data)
      let baselineStart = i;
      let baselinePoints = 0;
      for (let k = i - 1; k >= 0 && k >= i - 10; k--) {
        if (smoothedData[k].fuelLevel === null || smoothedData[k].smoothedFuelLevel === undefined) continue;
        baselineStart = k;
        baselinePoints++;
        if (baselinePoints >= 3) break; // Need at least 3 points for baseline
      }
      
      // Get baseline fuel level (average of last few points before ACC-off)
      const baselineWindow = smoothedData.slice(baselineStart, i).filter(p => 
        p.fuelLevel !== null && p.smoothedFuelLevel !== undefined
      );
      if (baselineWindow.length < 2) continue; // Need at least 2 baseline points
      
      const baselineFuel = baselineWindow.reduce((sum, p) => sum + (p.smoothedFuelLevel || 0), 0) / baselineWindow.length;
      const baselineVariance = Math.sqrt(
        baselineWindow.reduce((sum, p) => {
          const diff = (p.smoothedFuelLevel || 0) - baselineFuel;
          return sum + diff * diff;
        }, 0) / baselineWindow.length
      );
      
      // Get start and end fuel levels for this ACC-off period (using smoothed values)
      const periodStartFuel = smoothedData[i].smoothedFuelLevel!;
      const periodEndFuel = smoothedData[periodEnd].smoothedFuelLevel!;
      
      if (periodEndFuel === undefined || periodStartFuel === undefined) continue;
      
      // Step 4: Check if fuel was stable before ACC-off (not already decreasing)
      // If fuel was decreasing before ACC turned off, don't flag (it's not a leak during off period)
      const prePeriodDecrease = baselineFuel - periodStartFuel;
      const prePeriodDecreasePercent = baselineFuel > 0 ? (prePeriodDecrease / baselineFuel) * 100 : 0;
      
      // If fuel was already decreasing significantly before ACC-off, skip (not a new leak)
      if (prePeriodDecrease > FUEL_DECREASE_THRESHOLD_ABSOLUTE * 0.5 || prePeriodDecreasePercent > FUEL_DECREASE_THRESHOLD_PERCENT * 0.5) {
        continue;
      }
      
      // Step 5: Check for smooth increase before decrease (false positive filter)
      // If fuel increased smoothly before decreasing, it might be sensor fluctuation, not a leak
      let increasingBeforeDecrease = false;
      if (baselineWindow.length >= 2) {
        const firstBaseline = baselineWindow[0].smoothedFuelLevel || 0;
        const lastBaseline = baselineWindow[baselineWindow.length - 1].smoothedFuelLevel || 0;
        const baselineIncrease = lastBaseline - firstBaseline;
        const baselineIncreasePercent = firstBaseline > 0 ? (baselineIncrease / firstBaseline) * 100 : 0;
        
        // If there was a significant increase before the decrease, it's likely sensor fluctuation
        if (baselineIncrease > FUEL_DECREASE_THRESHOLD_ABSOLUTE * 0.7 || baselineIncreasePercent > FUEL_DECREASE_THRESHOLD_PERCENT * 0.7) {
          increasingBeforeDecrease = true;
        }
      }
      
      // Step 6: Calculate decrease DURING the ACC-off period (not from baseline)
      const periodDecrease = periodStartFuel - periodEndFuel;
      
      // Skip if fuel increased or stayed the same during the period
      if (periodDecrease <= 0) continue;
      
      // Calculate percentage decrease based on period start fuel level
      const percentDecrease = (periodDecrease / periodStartFuel) * 100;
      
      // Step 7: Check if decrease exceeds threshold (either percentage or absolute)
      const exceedsThreshold = 
        percentDecrease > FUEL_DECREASE_THRESHOLD_PERCENT || 
        periodDecrease > FUEL_DECREASE_THRESHOLD_ABSOLUTE;
      
      if (!exceedsThreshold) continue;
      
      // Step 8: Verify this is a sustained decrease during the period (not just noise)
      let decreasingSteps = 0;
      let totalDecreasingAmount = 0;
      let totalSteps = 0;
      let maxSingleDecrease = 0;
      
      for (let k = i + 1; k <= periodEnd; k++) {
        const prevPoint = smoothedData[k - 1];
        const currPoint = smoothedData[k];
        
        if (
          prevPoint.fuelLevel === null || 
          currPoint.fuelLevel === null ||
          prevPoint.smoothedFuelLevel === undefined ||
          currPoint.smoothedFuelLevel === undefined
        ) continue;
        
        totalSteps++;
        const stepChange = prevPoint.smoothedFuelLevel - currPoint.smoothedFuelLevel;
        if (stepChange > 0) {
          decreasingSteps++;
          totalDecreasingAmount += stepChange;
          maxSingleDecrease = Math.max(maxSingleDecrease, stepChange);
        }
      }
      
      if (totalSteps === 0) continue;
      
      // Step 9: Require that a significant ratio of steps show decrease
      const decreaseRatio = decreasingSteps / totalSteps;
      if (decreaseRatio < DECREASE_RATIO_THRESHOLD) continue;
      
      // Step 10: Additional checks to avoid false positives
      // If there was a smooth increase before decrease, require stronger evidence
      if (increasingBeforeDecrease) {
        // Require higher thresholds if there was an increase before
        if (percentDecrease < FUEL_DECREASE_THRESHOLD_PERCENT * 1.5 && 
            periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * 1.5) {
          continue;
        }
      }
      
      // Step 11: Check that the decrease is significant relative to baseline variance
      // If the decrease is within normal sensor fluctuation, don't flag
      if (baselineVariance > 0 && periodDecrease < baselineVariance * 2) {
        // Decrease is within 2x the baseline variance, might be noise
        // But still flag if it's a very clear sustained decrease
        if (decreaseRatio < 0.7 || periodDecrease < FUEL_DECREASE_THRESHOLD_ABSOLUTE * 1.5) {
          continue;
        }
      }
      
      // Step 12: Only flag if decrease happens DURING the period, not before
      // Flag only the points where fuel actually decreased during the ACC-off period
      for (let k = i; k <= periodEnd; k++) {
        if (smoothedData[k].accStatus === 0 && smoothedData[k].fuelLevel !== null) {
          // Only flag if this point is part of the decreasing trend
          // Check if fuel at this point is lower than period start
          const pointFuel = smoothedData[k].smoothedFuelLevel;
          if (pointFuel !== undefined && pointFuel < periodStartFuel) {
            flaggedIndices.add(k);
          }
        }
      }
    }
    
    // Apply flags to data
    const result = smoothedData.map((point, index) => ({
      ...point,
      fuelDecreasingWhileOff: flaggedIndices.has(index)
    }));
    
    // Return data with original fuel levels (not smoothed) but with flags
    return result.map(({ smoothedFuelLevel, ...rest }) => rest);
  };

  // Initial data fetch - only when sensor IDs or period changes
  // Thresholds changes are handled by applyThresholds button, not here
  // Using request ID and fetch flag to prevent duplicate calls (React StrictMode causes double render in dev)
  useEffect(() => {
    // If already fetching, skip (prevents duplicate calls from StrictMode)
    // Set flag immediately to prevent race condition
    if (isFetchingRef.current) {
      return;
    }
    
    // Set fetching flag IMMEDIATELY and synchronously before any async operations
    // This prevents the second StrictMode effect from also starting a fetch
    isFetchingRef.current = true;

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Increment request ID - this ensures only the latest request's results are processed
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    isMountedRef.current = true;

    // Fetch data with abort signal and request ID, including date range if custom
    // Use appliedDateRange if available (when Apply button was clicked), otherwise use current date states
    const dateRangeToUse = appliedDateRange || {};
    fetchChartData(
      selectedPeriod, 
      false, 
      abortController.signal, 
      currentRequestId,
      dateRangeToUse.from || fromDate || undefined,
      dateRangeToUse.to || toDate || undefined,
      dateRangeToUse.single || singleDate || undefined
    );

    // Cleanup: abort request if component unmounts or dependencies change
    return () => {
      abortController.abort();
      isMountedRef.current = false;
      // Reset fetch flag after a short delay to allow cleanup
      setTimeout(() => {
        if (requestIdRef.current === currentRequestId) {
          isFetchingRef.current = false;
        }
      }, 100);
    };
  }, [generatorSensorId, fuelSensorId, selectedPeriod, appliedDateRange]); // Removed fromDate, toDate, singleDate from dependencies

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
    // Clear applied date range when switching to non-custom period
    if (period !== 'custom') {
      setAppliedDateRange(null);
      setFromDate('');
      setToDate('');
      setSingleDate('');
    }
  };

  const handleRefresh = () => {
    // Use applied date range if available, otherwise use current date states
    const dateRangeToUse = appliedDateRange || {};
    fetchChartData(
      selectedPeriod, 
      true, 
      undefined, 
      undefined,
      dateRangeToUse.from || fromDate || undefined,
      dateRangeToUse.to || toDate || undefined,
      dateRangeToUse.single || singleDate || undefined
    );
  };

  // Calculate max date for "To Date" (1 month from "From Date")
  const getMaxToDate = (): string => {
    if (!fromDate) return '';
    const from = new Date(fromDate);
    const maxDate = new Date(from);
    maxDate.setMonth(maxDate.getMonth() + 1);
    return maxDate.toISOString().split('T')[0];
  };
  
  // Calculate min date for "To Date" (same as fromDate)
  const getMinToDate = (): string => {
    return fromDate || '';
  };

  const handleDateRangeApply = () => {
    if (fromDate && toDate) {
      // Validate date range (max 1 month)
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to.getTime() - from.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 31) {
        setError('Date range cannot exceed 31 days. Please select a shorter range.');
        return;
      }
      
      if (to < from) {
        setError('To date must be after From date.');
        return;
      }
      
      // Set applied date range - this will trigger the useEffect to fetch data
      setAppliedDateRange({ from: fromDate, to: toDate });
      setSelectedPeriod('custom');
      setSingleDate('');
      setShowDateRangePicker(false);
      setError(''); // Clear any previous errors
    }
  };

  const handleSingleDateApply = () => {
    if (singleDate) {
      // Set applied date range - this will trigger the useEffect to fetch data
      setAppliedDateRange({ single: singleDate });
      setSelectedPeriod('custom');
      setFromDate('');
      setToDate('');
      setShowSingleDatePicker(false);
      setError(''); // Clear any previous errors
    }
  };

  const handleClearCustomDates = () => {
    setFromDate('');
    setToDate('');
    setSingleDate('');
    setAppliedDateRange(null); // Clear applied dates
    setSelectedPeriod('today');
    setShowDateRangePicker(false);
    setShowSingleDatePicker(false);
  };

  const handleExport = () => {
    if (chartData.length === 0) {
      setError('No data available to export');
      return;
    }

    setExporting(true);
    try {
      // Prepare CSV data
      const headers = [
        'Timestamp',
        'Date',
        'Time',
        'Fuel Level (L)',
        'ACC Status',
        'ACC Status (Text)',
        'Alert: Fuel Decreasing While OFF',
        'Alert: Fuel Filling (Increasing)'
      ];

      const rows = chartData.map(point => {
        const date = new Date(point.timestamp);
        return [
          point.timestamp,
          point.formatted_date,
          point.formatted_time,
          point.fuelLevel !== null ? point.fuelLevel.toFixed(2) : 'N/A',
          point.accStatus,
          point.accStatus === 1 ? 'ON' : 'OFF',
          point.fuelDecreasingWhileOff ? 'YES' : 'NO',
          point.fuelIncreasing ? 'YES' : 'NO'
        ];
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape commas and quotes in cell values
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `generator-combined-chart-${generatorName.replace(/[^A-Za-z0-9]/g, '_')}-${selectedPeriod}-${dateStr}.csv`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      setError(''); // Clear any previous errors
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000); // Hide after 3 seconds
    } catch (err: any) {
      console.error('Error exporting data:', err);
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const formatTooltipLabel = (timestamp: string) => {
    try {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        // If timestamp is invalid, try to parse it differently
        return timestamp;
      }
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp || 'N/A';
    }
  };

  const formatFuelValue = (value: number | null) => {
    if (value === null) return 'N/A';
    return `${value.toFixed(1)}L`;
  };

  const formatACCStatus = (value: number) => {
    return value === 1 ? 'ON' : 'OFF';
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Get the full data point from the payload (recharts includes the full data object)
      const dataPoint = payload[0]?.payload as CombinedChartDataPoint | undefined;
      const fuelPayload = payload.find((p: any) => p.dataKey === 'fuelLevel');
      const accPayload = payload.find((p: any) => p.dataKey === 'accStatus');
      
      // Use the timestamp from the data point if available, otherwise try to find it by formatted_time
      const point = dataPoint || chartData.find(d => d.formatted_time === label || d.timestamp === label);
      const displayTimestamp = point?.timestamp || label;
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">
            {formatTooltipLabel(displayTimestamp)}
          </p>
          {fuelPayload && (
            <p className="text-sm text-blue-600 mb-1">
              <span className="font-medium">Fuel Level:</span> {formatFuelValue(fuelPayload.value)}
            </p>
          )}
          {accPayload && (
            <p className="text-sm text-green-600 mb-1">
              <span className="font-medium">ACC Status:</span> {formatACCStatus(accPayload.value)}
            </p>
          )}
          {point?.fuelDecreasingWhileOff && (
            <p className="text-xs text-red-600 mt-2 flex items-center">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Fuel decreasing while generator is OFF
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="bg-white rounded-xl p-8 max-w-md w-full mx-4"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading chart data...</p>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  const hasFuelData = chartData.some(d => d.fuelLevel !== null);
  const hasACCData = chartData.some(d => d.accStatus !== undefined);
  // Count unique alert periods (consecutive flagged points count as one alert)
  const alertCount = (() => {
    let count = 0;
    let inAlertPeriod = false;
    for (let i = 0; i < chartData.length; i++) {
      if (chartData[i].fuelDecreasingWhileOff && !inAlertPeriod) {
        count++;
        inAlertPeriod = true;
      } else if (!chartData[i].fuelDecreasingWhileOff) {
        inAlertPeriod = false;
      }
    }
    return count;
  })();

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[90vh] overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{generatorName}</h2>
              <p className="text-blue-100">Combined Fuel Level & ACC Status Chart</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-lg transition-colors ${
                  showSettings 
                    ? 'bg-white bg-opacity-30' 
                    : 'bg-white bg-opacity-20 hover:bg-opacity-30'
                }`}
                title="Detection Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleExport}
                disabled={exporting || chartData.length === 0}
                className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export to CSV"
              >
                <Download className={`w-5 h-5 ${exporting ? 'animate-pulse' : ''}`} />
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                title="Refresh Data"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Period Selection */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-2 mb-4">
            {periods.map((period) => {
              const IconComponent = period.icon;
              return (
                <button
                  key={period.value}
                  onClick={() => handlePeriodChange(period.value)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedPeriod === period.value
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{period.label}</span>
                </button>
              );
            })}
            
            {/* Date Range Selector */}
            <button
              onClick={() => {
                setShowDateRangePicker(!showDateRangePicker);
                setShowSingleDatePicker(false);
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                selectedPeriod === 'custom' && (fromDate || toDate)
                  ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>Date Range</span>
            </button>

            {/* Single Date Selector */}
            <button
              onClick={() => {
                setShowSingleDatePicker(!showSingleDatePicker);
                setShowDateRangePicker(false);
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                selectedPeriod === 'custom' && singleDate
                  ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>Single Day</span>
            </button>

            {/* Clear Custom Dates */}
            {(fromDate || toDate || singleDate) && (
              <button
                onClick={handleClearCustomDates}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all bg-red-100 text-red-700 hover:bg-red-200 border-2 border-red-300"
              >
                <X className="w-4 h-4" />
                <span>Clear</span>
              </button>
            )}
          </div>

          {/* Date Range Picker */}
          {showDateRangePicker && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="mb-2">
                <p className="text-xs text-gray-600">
                  Select a date range (maximum 31 days). Changes will only apply when you click "Apply".
                </p>
              </div>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      // If toDate is set and exceeds 1 month, clear it
                      if (toDate && e.target.value) {
                        const from = new Date(e.target.value);
                        const to = new Date(toDate);
                        const diffTime = Math.abs(to.getTime() - from.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        if (diffDays > 31) {
                          setToDate('');
                        }
                      }
                    }}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => {
                      const selectedToDate = e.target.value;
                      if (fromDate && selectedToDate) {
                        const from = new Date(fromDate);
                        const to = new Date(selectedToDate);
                        const diffTime = Math.abs(to.getTime() - from.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        if (diffDays > 31) {
                          setError('Date range cannot exceed 31 days. Please select a date within 1 month of the From date.');
                          return;
                        }
                        if (to < from) {
                          setError('To date must be after From date.');
                          return;
                        }
                        setError(''); // Clear error if valid
                      }
                      setToDate(selectedToDate);
                    }}
                    min={getMinToDate()}
                    max={getMaxToDate() || new Date().toISOString().split('T')[0]}
                    disabled={!fromDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {fromDate && (
                    <p className="text-xs text-gray-500 mt-1">
                      Max: {getMaxToDate() || 'N/A'}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleDateRangeApply}
                  disabled={!fromDate || !toDate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Apply
                </button>
              </div>
              {error && showDateRangePicker && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Single Date Picker */}
          {showSingleDatePicker && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="mb-2">
                <p className="text-xs text-gray-600">
                  Select a single date. Changes will only apply when you click "Apply".
                </p>
              </div>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
                  <input
                    type="date"
                    value={singleDate}
                    onChange={(e) => setSingleDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
                <button
                  onClick={handleSingleDateApply}
                  disabled={!singleDate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Detection Settings Panel */}
          {showSettings && (
            <div className="mt-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-bold text-gray-900 flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    Detection Thresholds (Local Only)
                  </h3>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Adjust thresholds to see real-time detection changes. Changes are local and not saved.
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={resetThresholds}
                    className="flex items-center space-x-1 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 bg-white rounded-md border border-gray-300 hover:border-gray-400 transition-colors"
                    title="Reset to defaults"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Reset</span>
                  </button>
                  <button
                    onClick={applyThresholds}
                    className="flex items-center space-x-1 px-3 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors font-medium"
                    title="Apply thresholds to chart"
                  >
                    <span>Apply</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Decrease Detection Section */}
                <div className="bg-white rounded-lg p-3 border border-red-200">
                  <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Decrease Detection
                  </h4>
                  {/* Fuel Decrease Threshold (Percentage) */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Decrease %: <span className="text-red-600 font-bold">{tempThresholds.fuelDecreaseThresholdPercent}%</span>
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="10"
                      step="0.5"
                      value={tempThresholds.fuelDecreaseThresholdPercent}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        fuelDecreaseThresholdPercent: parseFloat(e.target.value)
                      })}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>0.5%</span>
                      <span>10%</span>
                    </div>
                  </div>

                  {/* Fuel Decrease Threshold (Absolute) */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Decrease Absolute: <span className="text-red-600 font-bold">{tempThresholds.fuelDecreaseThresholdAbsolute}L</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      step="1"
                      value={tempThresholds.fuelDecreaseThresholdAbsolute}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        fuelDecreaseThresholdAbsolute: parseInt(e.target.value)
                      })}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>1L</span>
                      <span>50L</span>
                    </div>
                  </div>

                  {/* Minimum Consecutive Points */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Min Points: <span className="text-red-600 font-bold">{tempThresholds.minConsecutivePoints}</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      step="1"
                      value={tempThresholds.minConsecutivePoints}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        minConsecutivePoints: parseInt(e.target.value)
                      })}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>1</span>
                      <span>5</span>
                    </div>
                  </div>

                  {/* Smoothing Window */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Smoothing: <span className="text-red-600 font-bold">{tempThresholds.smoothingWindow} pts</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="7"
                      step="1"
                      value={tempThresholds.smoothingWindow}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        smoothingWindow: parseInt(e.target.value)
                      })}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>1</span>
                      <span>7</span>
                    </div>
                  </div>

                  {/* Decrease Ratio Threshold */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Decrease Ratio: <span className="text-red-600 font-bold">{(tempThresholds.decreaseRatioThreshold * 100).toFixed(0)}%</span>
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="0.9"
                      step="0.05"
                      value={tempThresholds.decreaseRatioThreshold}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        decreaseRatioThreshold: parseFloat(e.target.value)
                      })}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>10%</span>
                      <span>90%</span>
                    </div>
                  </div>
                </div>

                {/* Filling Detection Section */}
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    Filling Detection
                  </h4>

                  {/* Fuel Increase Threshold (Percentage) */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Increase %: <span className="text-green-600 font-bold">{(tempThresholds as any).fuelIncreaseThresholdPercent ?? 3.0}%</span>
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="10"
                      step="0.5"
                      value={(tempThresholds as any).fuelIncreaseThresholdPercent ?? 3.0}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        fuelIncreaseThresholdPercent: parseFloat(e.target.value)
                      } as any)}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>0.5%</span>
                      <span>10%</span>
                    </div>
                  </div>

                  {/* Fuel Increase Threshold (Absolute) */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Increase Absolute: <span className="text-green-600 font-bold">{(tempThresholds as any).fuelIncreaseThresholdAbsolute ?? 8.0}L</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      step="1"
                      value={(tempThresholds as any).fuelIncreaseThresholdAbsolute ?? 8.0}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        fuelIncreaseThresholdAbsolute: parseInt(e.target.value)
                      } as any)}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>1L</span>
                      <span>50L</span>
                    </div>
                  </div>

                  {/* Minimum Filling Duration */}
                  <div className="mb-2.5">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Min Duration: <span className="text-green-600 font-bold">{(tempThresholds as any).minFillingDurationMinutes ?? 2} min</span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="1"
                      value={(tempThresholds as any).minFillingDurationMinutes ?? 2}
                      onChange={(e) => setTempThresholds({
                        ...tempThresholds,
                        minFillingDurationMinutes: parseInt(e.target.value)
                      } as any)}
                      className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                      <span>1 min</span>
                      <span>10 min</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-xs text-blue-800">
                  <strong>Local Only:</strong> Changes apply immediately to the chart view but are not saved. Refresh will restore backend defaults.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Alerts Info - Consolidated notification */}
        {alerts.length > 0 && (
          <div className="px-6 py-3 bg-red-50 border-l-4 border-red-400">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  {alerts.length} fuel decrease alert{alerts.length !== 1 ? 's' : ''} detected: Fuel level decreased while generator was OFF
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Highlighted in <span className="font-semibold text-red-800">red with dashed line</span> on the chart below. Detection performed by backend daily cron job.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {(error || exportSuccess) && (
          <div className="px-6">
            {error && !exportSuccess && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              </div>
            )}
            {exportSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Download className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 text-sm font-medium">
                    Data exported successfully! Check your downloads folder.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chart Content */}
        <div className="p-6">
          {error && !exportSuccess ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-4">
                <X className="w-12 h-12 mx-auto mb-2" />
                <p className="text-lg font-medium">Error Loading Data</p>
                <p className="text-sm text-gray-600 mt-2">{error}</p>
              </div>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : chartData.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <TrendingUp className="w-12 h-12 mx-auto mb-2" />
                <p className="text-lg font-medium">No Data Available</p>
                <p className="text-sm text-gray-600 mt-2">
                  No sensor data found for the selected time period.
                </p>
              </div>
            </div>
          ) : (
            <>

              {/* Combined Chart */}
              <div className="h-96 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <defs>
                      {/* Normal fuel level gradient - Light blue */}
                      <linearGradient id="fuelGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.08}/>
                      </linearGradient>
                      {/* Alert gradient - Red for decreasing */}
                      <linearGradient id="fuelGradientAlert" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#dc2626" stopOpacity={0.5}/>
                        <stop offset="95%" stopColor="#dc2626" stopOpacity={0.25}/>
                      </linearGradient>
                      {/* Filling gradient - Bright green */}
                      <linearGradient id="fuelGradientFilling" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#059669" stopOpacity={0.5}/>
                        <stop offset="95%" stopColor="#059669" stopOpacity={0.25}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" strokeOpacity={0.5} />
                    <XAxis 
                      dataKey="formatted_time"
                      stroke="#4b5563"
                      fontSize={11}
                      fontWeight={500}
                      tickLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      tick={{ fill: '#4b5563' }}
                    />
                    {/* Fuel Level Y-Axis (Left) - Deep Blue */}
                    <YAxis 
                      yAxisId="fuel"
                      orientation="left"
                      stroke="#1e40af"
                      fontSize={11}
                      fontWeight={600}
                      tickLine={false}
                      label={{ value: 'Fuel Level (L)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#1e40af', fontWeight: 'bold' } }}
                      tickFormatter={(value) => `${value}L`}
                      tick={{ fill: '#1e40af', fontWeight: 500 }}
                    />
                    {/* ACC Status Y-Axis (Right) - Dark Green */}
                    <YAxis 
                      yAxisId="acc"
                      orientation="right"
                      stroke="#047857"
                      fontSize={11}
                      fontWeight={600}
                      tickLine={false}
                      domain={[0, 1]}
                      ticks={[0, 1]}
                      tickFormatter={(value) => value === 1 ? 'ON' : 'OFF'}
                      label={{ value: 'ACC Status', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fill: '#047857', fontWeight: 'bold' } }}
                      tick={{ fill: '#047857', fontWeight: 500 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="line"
                    />
                    {/* Fuel Level - Area Chart with conditional styling for alerts */}
                    {hasFuelData && (
                      <>
                        {/* Background area for normal fuel level - Light blue */}
                        <Area
                          yAxisId="fuel"
                          type="monotone"
                          dataKey="fuelLevelNormal"
                          name="Fuel Level (Normal)"
                          stroke="#3b82f6"
                          strokeWidth={2.5}
                          fill="url(#fuelGradient)"
                          dot={false}
                          activeDot={false}
                          connectNulls={false}
                        />
                        {/* Alert area - highlighted in red when decreasing while off - RED DASHED */}
                        <Area
                          yAxisId="fuel"
                          type="monotone"
                          dataKey="fuelLevelAlert"
                          name="⚠️ Fuel Decreasing (Generator OFF)"
                          stroke="#dc2626"
                          strokeWidth={4}
                          strokeDasharray="8 4"
                          fill="url(#fuelGradientAlert)"
                          dot={{ fill: '#dc2626', strokeWidth: 2, stroke: '#fff', r: 5 }}
                          activeDot={{ r: 8, fill: '#dc2626', stroke: '#fff', strokeWidth: 2 }}
                          connectNulls={true}
                          isAnimationActive={true}
                        />
                        {/* Fuel Filling (Increasing) - Highlighted prominently - BRIGHT GREEN SOLID */}
                        <Area
                          yAxisId="fuel"
                          type="monotone"
                          dataKey="fuelLevelFilling"
                          name="⬆️ Fuel Filling"
                          stroke="#059669"
                          strokeWidth={4}
                          fill="url(#fuelGradientFilling)"
                          dot={{ fill: '#059669', r: 6, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 8, fill: '#059669', stroke: '#fff', strokeWidth: 2 }}
                          connectNulls={true}
                          isAnimationActive={true}
                        />
                        {/* Additional bold line overlay for better visibility of filling spikes */}
                        <Line
                          yAxisId="fuel"
                          type="monotone"
                          dataKey="fuelLevelFilling"
                          stroke="#059669"
                          strokeWidth={3}
                          strokeDasharray="0"
                          dot={{ fill: '#059669', r: 5, strokeWidth: 2, stroke: '#fff' }}
                          activeDot={{ r: 7, fill: '#059669', stroke: '#fff', strokeWidth: 2 }}
                          connectNulls={true}
                        />
                        {/* Main fuel level line - DEEP BLUE SOLID - Most prominent */}
                        <Line
                          yAxisId="fuel"
                          type="monotone"
                          dataKey="fuelLevel"
                          name="Fuel Level"
                          stroke="#1e40af"
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 7, fill: '#1e40af', stroke: '#fff', strokeWidth: 2 }}
                          connectNulls={true}
                        />
                      </>
                    )}
                    {/* ACC Status - Step Chart - EMERALD GREEN with distinct style */}
                    {hasACCData && (
                      <Line
                        yAxisId="acc"
                        type="stepAfter"
                        dataKey="accStatus"
                        name="ACC Status"
                        stroke="#047857"
                        strokeWidth={4}
                        strokeDasharray="0"
                        dot={false}
                        activeDot={{ r: 7, fill: '#047857', stroke: '#fff', strokeWidth: 2 }}
                      />
                    )}
                    {/* Reference line for ACC OFF */}
                    <ReferenceLine yAxisId="acc" y={0.5} stroke="#ef4444" strokeDasharray="2 2" strokeOpacity={0.5} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Chart Statistics - All on one line */}
              {chartData.length > 0 && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-1">Data Points</p>
                    <p className="text-2xl font-bold text-gray-900">{chartData.length.toLocaleString()}</p>
                  </div>
                  {hasFuelData && (
                    <>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <p className="text-xs font-medium text-blue-600 mb-1">Avg Fuel Level</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {formatFuelValue(
                            chartData
                              .filter(d => d.fuelLevel !== null)
                              .reduce((sum, point) => sum + (point.fuelLevel || 0), 0) /
                            chartData.filter(d => d.fuelLevel !== null).length
                          )}
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <p className="text-xs font-medium text-purple-600 mb-1">Max Fuel Level</p>
                        <p className="text-2xl font-bold text-purple-900">
                          {formatFuelValue(
                            Math.max(...chartData.filter(d => d.fuelLevel !== null).map(d => d.fuelLevel || 0))
                          )}
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                        <p className="text-xs font-medium text-orange-600 mb-1">Min Fuel Level</p>
                        <p className="text-2xl font-bold text-orange-900">
                          {formatFuelValue(
                            Math.min(...chartData.filter(d => d.fuelLevel !== null).map(d => d.fuelLevel || 0))
                          )}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
                        <p className="text-xs font-medium text-green-600 mb-1">Total Fuel Filled</p>
                        <p className="text-2xl font-bold text-green-900">
                          {formatFuelValue(calculateTotalFilling(chartData, fillingAlerts))}
                        </p>
                      </div>
                      {consumptionRecords.length > 0 && (
                        <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-300">
                          <p className="text-xs font-medium text-indigo-600 mb-1">Total Consumption</p>
                          <p className="text-2xl font-bold text-indigo-900">
                            {formatFuelValue(
                              consumptionRecords.reduce((sum: number, record: any) => sum + (record.fuel_consumed || 0), 0)
                            )}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  {hasACCData && (
                    <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-300">
                      <p className="text-xs font-medium text-indigo-600 mb-1">ACC Run Hours</p>
                      <p className="text-2xl font-bold text-indigo-900">
                        {(() => {
                          // First try backend data
                          const backendTotal = accRunHours.length > 0
                            ? accRunHours.reduce((sum: number, record: any) => sum + (record.total_run_hours || 0), 0)
                            : 0;
                          
                          // Always calculate from chart data as well (for real-time accuracy)
                          const calculated = calculateAccRunHours(chartData);
                          
                          // Use the larger value (backend might be for previous day, calculated is current)
                          const finalTotal = Math.max(backendTotal, calculated);
                          
                          return finalTotal > 0 ? finalTotal.toFixed(2) : '0.00';
                        })()}h
                      </p>
                      {accRunHours.length > 0 && (
                        <p className="text-xs text-indigo-600 mt-1">
                          {accRunHours.length} day{accRunHours.length !== 1 ? 's' : ''} recorded
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GeneratorCombinedChart;

